import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderNoteType } from '../pages/delivery/graphql/types/DeliveryTaskOrderType';
import { getOrderNoteContent } from '../utils/orderNoteUtils';

interface UseOrderNoteOptions {
    orderNote: OrderNoteType | null | undefined;
    currentLanguage: string;
}

interface UseOrderNoteReturn {
    noteContent: string;
    isNoteExpanded: boolean;
    isNoteTruncated: boolean;
    handleToggleNote: () => void;
    noteWrapperCollapsedRef: React.RefObject<HTMLDivElement>;
    noteTextCollapsedRef: React.RefObject<HTMLSpanElement>;
}

export const useOrderNote = ({ orderNote, currentLanguage }: UseOrderNoteOptions): UseOrderNoteReturn => {
    const [isNoteExpanded, setIsNoteExpanded] = useState(false);
    const [isNoteTruncated, setIsNoteTruncated] = useState(false);
    const noteWrapperCollapsedRef = useRef<HTMLDivElement | null>(null);
    const noteTextCollapsedRef = useRef<HTMLSpanElement | null>(null);

    const noteContent = useMemo(() => {
        return getOrderNoteContent(orderNote, currentLanguage);
    }, [orderNote, currentLanguage]);

    const handleToggleNote = useCallback(() => {
        if (!noteContent) {
            return;
        }
        setIsNoteExpanded(prev => !prev);
    }, [noteContent]);

    useEffect(() => {
        setIsNoteExpanded(false);
    }, [noteContent]);

    useEffect(() => {
        if (!noteContent || isNoteExpanded) {
            setIsNoteTruncated(false);
            return;
        }

        const wrapper = noteWrapperCollapsedRef.current;
        const text = noteTextCollapsedRef.current;

        if (!wrapper || !text) {
            setIsNoteTruncated(false);
            return;
        }

        const measure = () => {
            const indicatorWidth = 24;
            const availableWidth = wrapper.clientWidth - indicatorWidth;
            const truncated = text.scrollWidth > availableWidth;
            setIsNoteTruncated(truncated);
        };

        const timeoutId = setTimeout(() => {
            if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                window.requestAnimationFrame(measure);
            } else {
                measure();
            }
        }, 0);

        if (typeof window !== 'undefined') {
            const handleResize = () => {
                setTimeout(() => {
                    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
                        window.requestAnimationFrame(measure);
                    } else {
                        measure();
                    }
                }, 0);
            };
            window.addEventListener('resize', handleResize);
            return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', handleResize);
            };
        }

        return () => {
            clearTimeout(timeoutId);
        };
    }, [noteContent, isNoteExpanded]);

    return {
        noteContent,
        isNoteExpanded,
        isNoteTruncated,
        handleToggleNote,
        noteWrapperCollapsedRef,
        noteTextCollapsedRef,
    };
};

